import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { clientAPI, productAPI, syncAPI } from "@/services/api";
import {
  CheckCircle2,
  Clock,
  Key,
  Link,
  Loader2,
  Package,
  Play,
  Plus,
  RefreshCw,
  Trash2,
  Zap,
} from "lucide-react";
import {
  TailAdminDataTable,
  TailAdminMetricCard,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const getProductId = (product) => product?.productId || product?.id || product?._id || "";
const getCommissionRate = (product) => Number(product?.commission_rate ?? product?.commission ?? 0);
const getSubscriptionAmount = (product) => Number(product?.subscription_amount ?? product?.subscriptionAmount ?? 0);
const getStatus = (product) => product?.status || (product?.isActive ? "active" : "draft");

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
  }).format(amount || 0);


const ClientProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [activeProductId, setActiveProductId] = useState(null);

  const [editApiEndpoint, setEditApiEndpoint] = useState("");
  const [editWebhookUrl, setEditWebhookUrl] = useState("");
  const [editCommissionRate, setEditCommissionRate] = useState("");
  const [editSubscriptionAmount, setEditSubscriptionAmount] = useState("");
  const [editIconUrl, setEditIconUrl] = useState("");
  const [editIconFile, setEditIconFile] = useState(null);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const [productsRes, profileRes] = await Promise.all([
        productAPI.getProducts(),
        clientAPI.getProfile(),
      ]);
      setProducts(productsRes.data || []);
      setActiveProductId(profileRes.data?.active_product_id || null);
    } catch {
      toast.error("Failed to load offers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const summary = useMemo(() => {
    const active = products.filter((product) => getStatus(product) === "active").length;
    const configured = products.filter((product) => product.api_endpoint).length;
    const avgCommission = products.length
      ? products.reduce((sum, product) => sum + getCommissionRate(product), 0) / products.length
      : 0;

    return {
      active,
      configured,
      avgCommission,
      monthlyValue: products.reduce((sum, product) => sum + getSubscriptionAmount(product), 0),
    };
  }, [products]);

  const handleOpenSettings = (product) => {
    setSelectedProduct(product);
    setEditApiEndpoint(product.api_endpoint || "");
    setEditWebhookUrl(product.webhook_url || "");
    setEditCommissionRate((getCommissionRate(product) * 100).toString());
    setEditSubscriptionAmount(getSubscriptionAmount(product).toString() || "5000");
    setEditIconUrl(product.iconUrl || product.icon_url || "");
    setEditIconFile(null);
    setShowSettingsDialog(true);
  };

  const handleSaveSettings = async () => {
    const productId = getProductId(selectedProduct);
    if (!productId) return;

    setSaving(true);
    try {
      await productAPI.updateProduct(productId, {
        api_endpoint: editApiEndpoint || null,
        webhook_url: editWebhookUrl || null,
        commission_rate: Number(editCommissionRate) / 100,
        subscription_amount: Number(editSubscriptionAmount),
        iconUrl: editIconUrl || null,
      });
      if (editIconFile) {
        await productAPI.uploadIcon(productId, editIconFile);
      }
      toast.success("Settings saved");
      setShowSettingsDialog(false);
      fetchProducts();
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateKey = async (productId) => {
    if (!productId || !window.confirm("This will invalidate the current API key. Continue?")) return;

    try {
      await productAPI.regenerateKey(productId);
      toast.success("API key regenerated");
      fetchProducts();
    } catch {
      toast.error("Failed to regenerate key");
    }
  };

  const handleDeactivateProduct = async (productId) => {
    if (productId === activeProductId) {
      toast.error("Choose another active offer before deactivating this one");
      return;
    }
    if (!productId || !window.confirm("Deactivate this offer? It will no longer be active, but its campaign, payment, and reward history will be retained.")) return;

    try {
      await productAPI.deleteProduct(productId);
      toast.success("Offer deactivated");
      fetchProducts();
    } catch {
      toast.error("Failed to deactivate offer");
    }
  };

  const handleSetActive = async (productId) => {
    try {
      await clientAPI.setActiveProduct(productId);
      setActiveProductId(productId);
      toast.success("Active offer updated");
    } catch {
      toast.error("Failed to set active offer");
    }
  };

  const handleTriggerSync = async (productId) => {
    setSyncing(productId);
    try {
      await syncAPI.triggerSync(productId);
      toast.success("Sync triggered. Check back in a moment.");
    } catch {
      toast.error("Failed to trigger sync");
    } finally {
      setSyncing(null);
    }
  };

  return (
    <div className="space-y-6">
      <TailAdminPageHeader
        eyebrow="Founder workspace"
        title="Your offers"
        description="Manage your offers, then launch funded campaigns from each one."
        actions={
          <>
            <Button
              variant="outline"
              className="h-10 rounded-lg"
              onClick={fetchProducts}
              disabled={loading}
              data-testid="refresh-products-btn"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              className="h-10 rounded-lg"
              onClick={() => navigate("/client/prove-requests/create")}
              data-testid="create-product-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create offer
            </Button>
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <TailAdminMetricCard
          label="Total offers"
          value={products.length}
          icon={Package}
          tone="primary"
          helper="Offers in workspace"
          loading={loading}
        />
        <TailAdminMetricCard
          label="Active offers"
          value={summary.active}
          icon={CheckCircle2}
          tone="success"
          helper="Available to Agents"
          loading={loading}
        />
        <TailAdminMetricCard
          label="Connection ready"
          value={`${summary.configured}/${products.length || 0}`}
          icon={Link}
          tone="warning"
          helper="Offers connected"
          loading={loading}
        />
        <TailAdminMetricCard
          label="Average Agent reward rate"
          value={`${(summary.avgCommission * 100).toFixed(0)}%`}
          icon={Zap}
          tone="neutral"
          helper="Average Agent percentage"
          loading={loading}
        />
      </div>

      <TailAdminPanel
        title="Offer inventory"
        description="Every offer has its own connection settings and can contain multiple campaigns."
        action={
          <Button variant="outline" size="sm" className="rounded-lg" onClick={() => navigate("/client/prove-requests/create")}>
            <Plus className="mr-2 h-4 w-4" />
            Create with setup
          </Button>
        }
      >
        {loading || products.length > 0 ? (
          <>
            <div className="grid gap-4 lg:grid-cols-2">
              {loading ? (
                <TailAdminDataTable loading columns={[]} rows={[]} />
              ) : products.map((product) => {
                const productId = getProductId(product);
                const isActive = activeProductId === productId;
                const configured = Boolean(product.api_endpoint);
                const productStatus = getStatus(product);
                const isInactive = productStatus === "inactive";
                return (
                  <article key={productId} className="rounded-xl border border-gray-200 p-4" data-testid={`mobile-offer-${productId}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="break-words font-semibold text-gray-900">{product.name}</h3>
                        {product.description ? <p className="mt-1 text-sm leading-5 text-gray-500">{product.description}</p> : null}
                      </div>
                      <TailAdminStatusBadge tone={productStatus === "active" ? "success" : "neutral"}>{productStatus === "active" ? "Available" : isInactive ? "Inactive" : "Draft"}</TailAdminStatusBadge>
                    </div>
                    <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div className="rounded-lg bg-gray-50 p-3">
                        <dt className="text-xs text-gray-500">Agent reward</dt>
                        <dd className="mt-1 font-semibold text-gray-900">{(getCommissionRate(product) * 100).toFixed(0)}%</dd>
                      </div>
                      <div className="rounded-lg bg-gray-50 p-3">
                        <dt className="text-xs text-gray-500">Subscription value</dt>
                        <dd className="mt-1 font-semibold text-gray-900">{formatCurrency(getSubscriptionAmount(product))}</dd>
                      </div>
                    </dl>
                    <p className={`mt-3 inline-flex items-center gap-1 text-sm ${configured ? "text-emerald-600" : "text-amber-700"}`}>
                      {configured ? <CheckCircle2 className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                      {configured ? "Connection ready" : "Connection setup needed"}
                    </p>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      {!isActive && !isInactive ? <Button variant="outline" className="min-h-11" onClick={() => handleSetActive(productId)}>Use as default</Button> : null}
                      <Button variant="outline" className="min-h-11" onClick={() => navigate(`/client/products/${productId}/campaigns`)}>Campaigns</Button>
                      <details className="col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <summary className="cursor-pointer text-sm font-medium text-gray-700">For your developer</summary>
                        <p className="mt-2 text-xs leading-5 text-gray-500">Connection setup, credentials, and manual sync tools.</p>
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <Button variant="outline" className="min-h-11 bg-white" onClick={() => navigate(`/client/products/${productId}/integration`)}>Connection</Button>
                          <Button variant="outline" className="min-h-11 bg-white" onClick={() => handleOpenSettings(product)}>Connection settings</Button>
                          <Button variant="outline" className="col-span-2 min-h-11 bg-white" onClick={() => navigate('/client/webhook-logs')}>Delivery logs</Button>
                          {configured ? <Button variant="outline" className="col-span-2 min-h-11 bg-white" onClick={() => handleTriggerSync(productId)} disabled={syncing === productId}>{syncing === productId ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="mr-1 h-4 w-4" />}Check now</Button> : null}
                        </div>
                      </details>
                      <Button variant="ghost" className="col-span-2 min-h-11 text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => handleDeactivateProduct(productId)} disabled={isActive || isInactive} title={isActive ? "Choose another active offer before deactivating this one" : isInactive ? "This offer is already inactive" : undefined} aria-label={`Deactivate ${product.name} offer`}>
                        <Trash2 className="mr-1 h-4 w-4" /> Deactivate offer
                      </Button>
                      {isActive ? <p className="col-span-2 text-xs leading-5 text-gray-500">Choose another active offer before deactivating this one.</p> : null}
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        ) : (
          <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 p-8 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
              <Package className="h-7 w-7" />
            </div>
            <h3 className="text-lg font-semibold text-gray-950">No offers yet</h3>
            <p className="mt-2 max-w-md text-sm leading-6 text-gray-500">
              Create your first funded offer so Agents can promote it and Proovam can verify subscriptions.
            </p>
            <Button className="mt-5 rounded-lg" onClick={() => navigate("/client/prove-requests/create")}>
              <Plus className="mr-2 h-4 w-4" />
              Create offer
            </Button>
          </div>
        )}
      </TailAdminPanel>

      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Product Settings</DialogTitle>
            <DialogDescription>Configure API integration for {selectedProduct?.name}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-icon-url">Product icon (PNG upload or URL)</Label>
              <div className="flex items-center gap-3">
                {(editIconFile || editIconUrl) ? (
                  <img
                    src={editIconFile ? URL.createObjectURL(editIconFile) : editIconUrl}
                    alt="Product icon preview"
                    className="h-12 w-12 rounded-xl border border-gray-200 object-cover"
                  />
                ) : null}
                <Input
                  id="edit-icon-url"
                  type="url"
                  placeholder="https://example.com/icon.png"
                  value={editIconUrl}
                  onChange={(event) => setEditIconUrl(event.target.value)}
                />
              </div>
              <Input
                type="file"
                accept="image/png"
                onChange={(event) => setEditIconFile(event.target.files?.[0] || null)}
              />
              <p className="text-xs text-gray-500">PNG only, maximum 512KB. SVG is rejected.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-commission">Commission (%)</Label>
                <Input
                  id="edit-commission"
                  type="number"
                  value={editCommissionRate}
                  onChange={(event) => setEditCommissionRate(event.target.value)}
                  min={1}
                  max={100}
                  data-testid="edit-commission-rate"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-subscription">Subscription (NGN)</Label>
                <Input
                  id="edit-subscription"
                  type="number"
                  value={editSubscriptionAmount}
                  onChange={(event) => setEditSubscriptionAmount(event.target.value)}
                  min={100}
                  data-testid="edit-subscription-amount"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="api-endpoint">
                <Link className="mr-1 inline h-4 w-4" />
                API Endpoint URL
              </Label>
              <Input
                id="api-endpoint"
                type="url"
                placeholder="https://api.yourcompany.com/enrollments"
                value={editApiEndpoint}
                onChange={(event) => setEditApiEndpoint(event.target.value)}
                data-testid="edit-api-endpoint"
              />
              <p className="text-xs text-gray-500">
                Proovam will call GET /check-enrollment?phone_hash=... to verify subscriptions.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="webhook-url">Webhook URL</Label>
              <Input
                id="webhook-url"
                type="url"
                placeholder="https://yourcompany.com/webhooks/proovam"
                value={editWebhookUrl}
                onChange={(event) => setEditWebhookUrl(event.target.value)}
                data-testid="edit-webhook-url"
              />
            </div>

            <div className="border-t border-gray-100 pt-4">
              <Button
                variant="outline"
                size="sm"
                className="rounded-lg text-amber-600 hover:text-amber-700"
                onClick={() => handleRegenerateKey(getProductId(selectedProduct))}
              >
                <Key className="mr-2 h-4 w-4" />
                Regenerate API Key
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-product-settings">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientProducts;
